import {
  isBrowserToolName,
  isHttpExchangeTool,
  isMcpToolName,
} from '@terra/shared-chat-trace/trace-view/utils/tool-config.helpers';
import {
  detectEntityType,
  isMongoId,
  type EntityType,
  type ResolvedCredential,
  type ResolvedEndpoint,
  type ResolvedEntity,
  type ResolvedSignal,
} from '../context/EntityResolverContext';
import {
  filterMcpDisplayArgs,
  truncateArgsPreview,
} from '../components/tool-args-display.util';
import { clientFacingToolName } from '../../components/client-facing-tool-name';
import { summarizeSelectedToolNames } from './load-tools-output';
import { normalizeToolName } from './tool-config';

export type ResolveTraceEntity = (
  type: EntityType,
  id: string,
) => ResolvedEntity | undefined;

type InlineToolSummaryInput = {
  toolName: string;
  args: Record<string, unknown>;
  resolve: ResolveTraceEntity;
};

const UNRESOLVED_ID_PREVIEW_LENGTH = 8;
const SIGNAL_SUMMARY_MAX_LENGTH = 60;

const ENDPOINT_TOOL_NAMES = new Set([
  'get-endpoint-details',
  'endpoint-details',
  'endpoint-details-tool',
  'get-tracked-endpoint-schema',
  'endpoint-read',
]);

const AUTH_TOOL_NAMES = new Set([
  'get-authentication-details',
  'authentication-details',
  'authentication-details-tool',
  'login-tool',
]);

const TOOLS_WITHOUT_SUMMARY = new Set([
  'end-test',
  'todo-write',
  'save-neo-memories',
  'ai-discovery-complete',
  'ai-discovery-verdict',
]);

const QUERY_SUMMARY_MAX_LENGTH = new Map<string, number | undefined>([
  ['search-endpoints', undefined],
  ['coder', 60],
  ['sub-agent-coder', 60],
  ['sub-agent-intelligence', 60],
  ['navigate-web', 60],
  ['business-context-tool', 80],
  ['get-business-context', 80],
  ['get-neo-memories', 80],
]);

const ASK_CODEBASE_MAX_LENGTH = 90;
const INTERNAL_NETWORK_DESCRIPTION_MAX_LENGTH = 80;
const MCP_QUERY_MAX_LENGTH = 80;

function toNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  return trimmed.length > 0 ? trimmed : undefined;
}

function truncate(value: string, maxLength: number | undefined): string {
  return maxLength === undefined
    ? value
    : truncateArgsPreview(value, maxLength);
}

function shortId(id: string): string {
  return id.slice(0, UNRESOLVED_ID_PREVIEW_LENGTH);
}

function formatModeWithCenter(args: Record<string, unknown>): string {
  const center = toNonEmptyString(args.centerNodeId);

  return center ? `${String(args.mode)} · ${center}` : String(args.mode);
}

type ResolvedEntityFields = Partial<
  Record<
    keyof ResolvedEndpoint | keyof ResolvedSignal | keyof ResolvedCredential,
    unknown
  >
>;

function formatEndpointFields(
  fields: ResolvedEntityFields,
): string | undefined {
  const method = toNonEmptyString(fields.method);
  const path = toNonEmptyString(fields.path);

  if (!method || !path) {
    return undefined;
  }

  return `${method} ${toNonEmptyString(fields.host) ?? ''}${path}`;
}

function formatCredentialFields(
  fields: ResolvedEntityFields,
): string | undefined {
  const role = toNonEmptyString(fields.role) ?? toNonEmptyString(fields.type);
  const username = toNonEmptyString(fields.username);

  if (role && username) {
    return `${role}:${username}`;
  }

  return username;
}

export function formatResolvedTraceEntity(entity: ResolvedEntity): string {
  const fields: ResolvedEntityFields = entity;
  const description = toNonEmptyString(fields.description);

  return (
    formatEndpointFields(fields) ??
    (description
      ? truncateArgsPreview(description, SIGNAL_SUMMARY_MAX_LENGTH)
      : undefined) ??
    formatCredentialFields(fields) ??
    toNonEmptyString(fields.name) ??
    entity.id
  );
}

function describeEntityId({
  type,
  value,
  resolve,
}: {
  type: EntityType;
  value: unknown;
  resolve: ResolveTraceEntity;
}): string | undefined {
  if (isMongoId(value)) {
    const entity = resolve(type, value);

    return entity ? formatResolvedTraceEntity(entity) : shortId(value);
  }

  return toNonEmptyString(value);
}

function getMcpSummary({
  normalizedName,
  args,
}: {
  normalizedName: string;
  args: Record<string, unknown>;
}): string | undefined {
  const query = toNonEmptyString(args.query);
  const term = toNonEmptyString(args.term);
  const hasMode = toNonEmptyString(args.mode) !== undefined;

  if (normalizedName.includes('cypher') && query) {
    return truncateArgsPreview(query, MCP_QUERY_MAX_LENGTH);
  }

  if (normalizedName.includes('search') && term) {
    return term;
  }

  if (normalizedName.includes('graph') && hasMode) {
    return formatModeWithCenter(args);
  }

  if (query) {
    return truncateArgsPreview(query, MCP_QUERY_MAX_LENGTH);
  }

  if (term) {
    return term;
  }

  return hasMode ? formatModeWithCenter(args) : undefined;
}

function getHttpSummary(args: Record<string, unknown>): string | undefined {
  const url = toNonEmptyString(args.url);

  if (!url) {
    return undefined;
  }

  const method = toNonEmptyString(args.method)?.toUpperCase() ?? 'GET';

  return `${method} ${url}`;
}

function getInternalNetworkSummary({
  normalizedName,
  args,
}: {
  normalizedName: string;
  args: Record<string, unknown>;
}): string | undefined {
  switch (normalizedName) {
    case 'remote-c2-command':
      return toNonEmptyString(args.command);
    case 'list-ad-entities':
      return toNonEmptyString(args.entityKind);
    case 'query-ad-graph':
      return toNonEmptyString(args.mode)
        ? formatModeWithCenter(args)
        : undefined;
    case 'get-ad-entity-details':
    case 'get-ad-entity-relationships':
      return toNonEmptyString(args.nodeId);
    case 'create-internal-network-discovery-signal': {
      const description = toNonEmptyString(args.description);

      return description
        ? truncateArgsPreview(
            description,
            INTERNAL_NETWORK_DESCRIPTION_MAX_LENGTH,
          )
        : undefined;
    }

    default:
      return undefined;
  }
}

function getEntityIdsSummary({
  args,
  resolve,
}: {
  args: Record<string, unknown>;
  resolve: ResolveTraceEntity;
}): string | undefined {
  const parts = Object.entries(args).flatMap(([key, value]) => {
    const type = isMongoId(value) ? detectEntityType(key) : null;
    const description = type
      ? describeEntityId({ type, value, resolve })
      : undefined;

    return description ? [description] : [];
  });

  return parts.length > 0 ? parts.join(' · ') : undefined;
}

const BROWSER_TEXT_SUMMARY_MAX_LENGTH = 60;

function formatBrowserTarget(
  args: Record<string, unknown>,
): string | undefined {
  const ref = toNonEmptyString(args.ref);

  if (ref) {
    return ref;
  }

  const { x, y } = args;

  if (typeof x === 'number' && typeof y === 'number') {
    return `(${x}, ${y})`;
  }

  return toNonEmptyString(args.text);
}

function formatBrowserTypedValue(
  args: Record<string, unknown>,
): string | undefined {
  const text = toNonEmptyString(args.text);

  if (text) {
    return truncateArgsPreview(text, BROWSER_TEXT_SUMMARY_MAX_LENGTH);
  }

  if (Array.isArray(args.fields)) {
    const count = args.fields.length;

    return `${count} field${count === 1 ? '' : 's'}`;
  }

  return undefined;
}

function getBrowserToolSummary({
  normalizedName,
  args,
}: {
  normalizedName: string;
  args: Record<string, unknown>;
}): string | undefined {
  switch (normalizedName) {
    case 'browser-open': {
      const url = toNonEmptyString(args.startUrl) ?? toNonEmptyString(args.url);
      const freshSession = args.newSession === true ? 'new session' : undefined;

      return [url, freshSession].filter(Boolean).join(' · ') || undefined;
    }

    case 'browser-navigate':
      return toNonEmptyString(args.url);
    case 'browser-click':
    case 'browser-hover':
    case 'browser-select-option':
      return formatBrowserTarget(args);
    case 'browser-type':
    case 'browser-fill-form':
      return formatBrowserTypedValue(args);
    case 'browser-press-key':
      return toNonEmptyString(args.key);
    case 'browser-handle-dialog': {
      const outcome = args.accept === true ? 'accepted' : 'dismissed';
      const promptText = toNonEmptyString(args.promptText);

      return promptText ? `${outcome} · ${promptText}` : outcome;
    }

    case 'browser-scroll': {
      const direction = toNonEmptyString(args.direction);
      const amount =
        typeof args.amount === 'number' ? String(args.amount) : undefined;

      return [direction, amount].filter(Boolean).join(' · ') || undefined;
    }

    case 'browser-history':
      return toNonEmptyString(args.action);
    case 'browser-tabs':
      return toNonEmptyString(args.mode);
    case 'browser-wait-for':
      return toNonEmptyString(args.text) ?? toNonEmptyString(args.selector);
    case 'browser-find':
      return toNonEmptyString(args.query) ?? toNonEmptyString(args.text);
    case 'browser-evaluate': {
      const expression = toNonEmptyString(args.expression);

      return expression
        ? truncateArgsPreview(expression, BROWSER_TEXT_SUMMARY_MAX_LENGTH)
        : undefined;
    }

    case 'browser-network-request':
      return toNonEmptyString(args.id);
    default:
      return undefined;
  }
}

export function getInlineToolSummary({
  toolName,
  args,
  resolve,
}: InlineToolSummaryInput): string | undefined {
  const displayArgs = filterMcpDisplayArgs({ toolName, args });

  if (Object.keys(displayArgs).length === 0) {
    return undefined;
  }

  const normalizedName = normalizeToolName(toolName);

  if (TOOLS_WITHOUT_SUMMARY.has(normalizedName)) {
    return undefined;
  }

  if (normalizedName === 'load-tools') {
    return summarizeSelectedToolNames({
      args: displayArgs,
      formatName: clientFacingToolName,
    });
  }

  if (isBrowserToolName(toolName)) {
    return getBrowserToolSummary({ normalizedName, args: displayArgs });
  }

  if (isMcpToolName(toolName)) {
    return getMcpSummary({ normalizedName, args: displayArgs });
  }

  if (isHttpExchangeTool(toolName)) {
    return getHttpSummary(displayArgs);
  }

  if (ENDPOINT_TOOL_NAMES.has(normalizedName)) {
    return describeEntityId({
      type: 'endpoint',
      value: displayArgs.endpointId,
      resolve,
    });
  }

  if (AUTH_TOOL_NAMES.has(normalizedName)) {
    return describeEntityId({
      type: 'credential',
      value: displayArgs.credentialId,
      resolve,
    });
  }

  if (normalizedName === 'ask-codebase') {
    const question = toNonEmptyString(displayArgs.question);

    return question ? truncate(question, ASK_CODEBASE_MAX_LENGTH) : undefined;
  }

  if (QUERY_SUMMARY_MAX_LENGTH.has(normalizedName)) {
    const query = toNonEmptyString(displayArgs.query);

    return query
      ? truncate(query, QUERY_SUMMARY_MAX_LENGTH.get(normalizedName))
      : undefined;
  }

  return (
    getInternalNetworkSummary({ normalizedName, args: displayArgs }) ??
    getEntityIdsSummary({ args: displayArgs, resolve })
  );
}
